import api from '../request'
import { API_BASE_URL, SYSTEM_BASE_URL } from '../../const/EndPoints'
import { SUBSCRIPTION_ENDPOINTS } from '../../const/SubscriptionEndpoints'
import { Subscription, Plan, BillingPeriod, CheckoutResponse, UsageStats, AIMLUsageStats, IdentityUsageStats, CancelReason, PlanCatalog } from '../../models/Subscription'

const UPGRADE_SUCCESS_URL = `${window.location.origin}/dashboard/welcome`
const UPGRADE_CANCEL_URL = `${window.location.origin}/dashboard/upgrade`

export const getSubscription = async (): Promise<Subscription> => {
  const response = await api.get(
    API_BASE_URL + SUBSCRIPTION_ENDPOINTS.GET
  )
  return response.data
}

export const createCheckout = async (
  plan: Plan,
  billingPeriod: BillingPeriod
): Promise<CheckoutResponse> => {
  const response = await api.post(
    API_BASE_URL + SUBSCRIPTION_ENDPOINTS.CHECKOUT,
    {
      plan,
      billingPeriod,
      successUrl: UPGRADE_SUCCESS_URL,
      cancelUrl: UPGRADE_CANCEL_URL,
    }
  )
  return response.data
}

export const createPortalSession = async (): Promise<{ portalUrl: string }> => {
  const response = await api.post(
    API_BASE_URL + SUBSCRIPTION_ENDPOINTS.PORTAL,
    {}
  )
  return response.data
}

export const getPlanCatalog = async (): Promise<PlanCatalog> => {
  const response = await api.get(
    API_BASE_URL + SUBSCRIPTION_ENDPOINTS.PLANS
  )
  return response.data
}

export const getUsageStats = async (): Promise<UsageStats> => {
  // Fetch from both services in parallel — each returns only what it owns
  // AIML: chat/voice/handoff counters + limits (Redis + config)
  // Identity: connected devices (Postgres connector_tokens)
  const [aimlRes, identityRes] = await Promise.all([
    api.get(SYSTEM_BASE_URL + '/v1/usage').catch(() => ({ data: {} })),
    api.get(API_BASE_URL + SUBSCRIPTION_ENDPOINTS.USAGE).catch(() => ({ data: {} })),
  ])

  const aiml: Partial<AIMLUsageStats> = aimlRes.data ?? {}
  const identity: Partial<IdentityUsageStats> = identityRes.data ?? {}

  return {
    chatMessagesToday: aiml.chatMessagesToday ?? 0,
    chatMessagesLimit: aiml.chatMessagesLimit ?? 15,
    voiceThisMonth: aiml.voiceThisMonth ?? 0,
    voiceLimit: aiml.voiceLimit ?? 3,
    handoffsThisMonth: aiml.handoffsThisMonth ?? 0,
    handoffsLimit: aiml.handoffsLimit ?? 2,
    timelineDataPoints: aiml.timelineDataPoints ?? 0,
    timelineFirstDate: aiml.timelineFirstDate ?? null,
    connectedDevices: identity.connectedDevices ?? 0,
  }
}

export const pauseSubscription = async (months: number): Promise<void> => {
  await api.post(
    API_BASE_URL + SUBSCRIPTION_ENDPOINTS.PAUSE,
    { months }
  )
}

export const cancelSubscription = async (
  reason: CancelReason,
  detail?: string,
  immediate?: boolean
): Promise<void> => {
  await api.post(
    API_BASE_URL + SUBSCRIPTION_ENDPOINTS.CANCEL,
    { reason, detail, immediate: immediate ?? false }
  )
}
